import React, { useEffect, useState, useMemo } from "react";
import {
  Collapse,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Popconfirm,
  Spin,
  Empty,
  Pagination,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import AutoCompleteAddress from "../../../components/AutoCompleteAddress";
import { buildAdminCollapseItem } from "../../../components/admin/AdminCollapseItem";
import {
  _get_admin_fulfillment_centers,
  _create_fulfillment_center,
  _update_fulfillment_center,
  _delete_fulfillment_center,
} from "../../../common/axios_services";
import {
  showSuccessNotification,
  showErrorNotification,
} from "../../../common/utils/notification";

const { Option } = Select;

export default function AdminFulfillmentCenters() {
  const [fcs, setFcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFc, setEditingFc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const fetchFcs = async () => {
    setLoading(true);
    try {
      const res = await _get_admin_fulfillment_centers();
      const raw = res.data?.data?.data || res.data?.data || res.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setFcs(list);
    } catch (err) {
      showErrorNotification("Failed to fetch fulfillment centers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFcs();
  }, []);

  const handleOpenAddModal = () => {
    setEditingFc(null);
    form.resetFields();
    form.setFieldsValue({ status: "ACTIVE" });
    setModalVisible(true);
  };

  const handleOpenEditModal = (fc) => {
    setEditingFc(fc);
    form.setFieldsValue({
      name: fc.name,
      address: fc.address,
      latitude: fc.latitude,
      longitude: fc.longitude,
      state: fc.state,
      lga: fc.lga,
      contactPerson: fc.contactPerson,
      contactPhone: fc.contactPhone,
      operatingHours: fc.operatingHours,
      status: fc.status,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await _delete_fulfillment_center(id);
      if (res.data?.success || res.status === 200) {
        showSuccessNotification("Fulfillment center deleted successfully.");
        fetchFcs();
      }
    } catch (err) {
      showErrorNotification("Failed to delete fulfillment center.");
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingFc) {
        const res = await _update_fulfillment_center(editingFc.id, values);
        if (res.data?.success || res.status === 200 || res.status === 201) {
          showSuccessNotification("Fulfillment center updated successfully.");
          setModalVisible(false);
          form.resetFields();
          await fetchFcs();
        } else {
          showErrorNotification(res.data?.message || "Failed to update fulfillment center.");
        }
      } else {
        const res = await _create_fulfillment_center(values);
        if (res.data?.success || res.status === 200 || res.status === 201) {
          showSuccessNotification("Fulfillment center created successfully.");
          setModalVisible(false);
          form.resetFields();
          await fetchFcs();
        } else {
          showErrorNotification(res.data?.message || "Failed to create fulfillment center.");
        }
      }
    } catch (err) {
      showErrorNotification(
        err.response?.data?.message || "Failed to save fulfillment center.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const filteredFcs = useMemo(() => {
    let result = fcs;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((fc) => {
        const name = (fc.name || "").toLowerCase();
        const address = (fc.address || "").toLowerCase();
        const lga = (fc.lga || "").toLowerCase();
        const state = (fc.state || "").toLowerCase();
        return (
          name.includes(q) ||
          address.includes(q) ||
          lga.includes(q) ||
          state.includes(q)
        );
      });
    }
    return result;
  }, [fcs, searchQuery]);

  const paginatedFcs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredFcs.slice(start, start + pageSize);
  }, [filteredFcs, currentPage, pageSize]);

  const renderCollapseItems = () => {
    return paginatedFcs.map((fc, index) => {
      const locationText = [fc.lga, fc.state].filter(Boolean).join(", ");
      const headerTitle = `${fc.name}${locationText ? ` - ${locationText}` : ""}`;

      const items = [
        {
          label: "Status",
          value: (
            <Tag color={fc.status === "ACTIVE" ? "green" : "red"}>
              {fc.status}
            </Tag>
          ),
        },
        {
          label: "FC Name",
          value: <span className="font-semibold text-gray-900">{fc.name}</span>,
        },
        { label: "Address", value: fc.address },
        { label: "LGA / State", value: locationText || "—" },
        { label: "Operating Hours", value: fc.operatingHours || "—" },
        { label: "Contact Person", value: fc.contactPerson || "—" },
        { label: "Contact Phone", value: fc.contactPhone || "—" },
      ];

      const actions = (
        <>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(fc)}
            className="font-gilroyBold"
          >
            Edit FC
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this Fulfillment Center?"
            onConfirm={() => handleDelete(fc.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              className="font-gilroyBold"
            >
              Delete FC
            </Button>
          </Popconfirm>
        </>
      );

      return buildAdminCollapseItem({
        key: fc.id || index.toString(),
        headerTitle,
        items,
        actions,
      });
    });
  };

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Fulfillment Centers</h2>
            <p className="text-gray-500">
              Manage fulfillment centers for Wholesale pickup locations
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenAddModal}
            style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
            className="font-semibold"
          >
            Add Fulfillment Center
          </Button>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <Input
            placeholder="Search by FC name, address, LGA, state..."
            prefix={<SearchOutlined className="text-gray-400" />}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            allowClear
            size="large"
          />
        </div>

        {!loading && paginatedFcs.length === 0 ? (
          <div className="bg-white p-8 rounded-xl border border-gray-200 text-center">
            <Empty description="No fulfillment centers found." />
          </div>
        ) : (
          <>
            <Collapse
              className="admin-collapse bg-transparent border-0"
              items={renderCollapseItems()}
            />

            {filteredFcs.length > 0 && (
              <div className="flex justify-center mt-6">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={filteredFcs.length}
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  }}
                  showSizeChanger
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} fulfillment centers`
                  }
                />
              </div>
            )}
          </>
        )}

        <Modal
          title={
            <span className="font-bold text-lg">
              {editingFc ? "Edit Fulfillment Center" : "Add New Fulfillment Center"}
            </span>
          }
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          confirmLoading={submitting}
          okText={editingFc ? "Update FC" : "Create FC"}
          width={600}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
            <Form.Item
              name="name"
              label="Fulfillment Center Name"
              rules={[{ required: true, message: "Please enter FC name" }]}
            >
              <Input placeholder="e.g. Ikeja Fulfillment Centre" />
            </Form.Item>

            <Form.Item
              name="address"
              label="Street Address (Google Maps)"
              rules={[{ required: true, message: "Please select street address" }]}
            >
              <AutoCompleteAddress
                placeholder="Search address with Google Maps..."
                value={form.getFieldValue("address")}
                onChange={(val) => form.setFieldsValue({ address: val })}
                onAddressSelect={(details) => {
                  form.setFieldsValue({
                    address: details.formattedAddress || details.fullPlaceName || details.address,
                    latitude: details.latitude,
                    longitude: details.longitude,
                    state: details.state || "",
                    lga: details.lga || details.city || "",
                  });
                }}
              />
            </Form.Item>

            <div className="grid grid-cols-2 gap-3">
              <Form.Item name="lga" label="LGA">
                <Input placeholder="e.g. Ikeja" />
              </Form.Item>
              <Form.Item name="state" label="State">
                <Input placeholder="e.g. Lagos" />
              </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Form.Item name="contactPerson" label="Contact Person">
                <Input placeholder="e.g. Mr. Joseph" />
              </Form.Item>
              <Form.Item name="contactPhone" label="Contact Phone">
                <Input placeholder="e.g. 08012345678" />
              </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Form.Item name="operatingHours" label="Operating Hours">
                <Input placeholder="e.g. Mon-Sat: 8am - 6pm" />
              </Form.Item>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select>
                  <Option value="ACTIVE">ACTIVE</Option>
                  <Option value="INACTIVE">INACTIVE</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item name="latitude" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="longitude" hidden>
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Spin>
  );
}
